import { ManifestError, ManifestProvider, ManifestWithAccountDetails } from "@deliverr/sortation-client";
import { initiateDownloadFromUrl } from "@deliverr/ui";
import { createSuccessNotification } from "@deliverr/ui-facility";
import { useCommonFlow } from "@deliverr/ui-facility/lib-facility/flow/useCommonFlow";
import { useCallback, useState } from "react";
import { useAsync } from "react-use";
import { useRecoilValue } from "recoil";
import { sortationIdState } from "sortation/base/sortationIdState";
import { useSortationClient } from "sortation/base/useSortationClient";
import { useSortationModal } from "sortation/components/SortationModal";
import { TOAST_AUTO_CLOSE } from "sortation/utils/toastAutoClose";
import { manifestMessageMap, ManifestModal } from "./modals";
import { ManifestResponse } from "@deliverr/carrier-network-types";
import { ManifestCarrier } from "sortation/utils/ManifestCarrier";

export const useManifests = (carrier: ManifestCarrier) => {
  const { addAutoCloseNotification, emitFlash } = useCommonFlow();
  const { showModal } = useSortationModal();
  const sortationId = useRecoilValue(sortationIdState);
  const sortationClient = useSortationClient();

  const [manifests, setManifests] = useState<ManifestWithAccountDetails[]>([]);
  const [manifestsCurrentlyDownloading, setManifestsCurrentlyDownloading] = useState<number[]>([]);
  const [epostManifests, setEpostManifests] = useState<ManifestResponse[]>([]);

  const handleError = useCallback(
    (err) => {
      const errCode = err.message;

      let modal: ManifestModal;

      switch (errCode) {
        case ManifestError.FAILED_TO_GET_MANIFEST:
          modal = ManifestModal.FAILED_TO_GET_MANIFEST;
          break;
        default:
          modal = ManifestModal.UNKNOWN_ERROR;
          break;
      }

      emitFlash("DANGER");
      showModal(modal, manifestMessageMap[modal]);
    },
    [emitFlash, showModal]
  );

  // Initial data fetch
  const { loading: isLoading } = useAsync(async () => {
    try {
      if (!sortationId) {
        return;
      }
      if (carrier === ManifestCarrier.DHL) {
        const fetchedManifests = await sortationClient.getManifests(sortationId);
        setManifests(fetchedManifests);
      } else if (carrier === ManifestCarrier.RRDONNELLEY) {
        const epostManifestsResponse = await sortationClient.getGlobalEGeneratedManifests({
          sortationCenterId: sortationId,
          carrier,
        });
        // Parse dispatchedAt as Date
        const parsedEpostManifests = epostManifestsResponse.map((manifest) => ({
          ...manifest,
          dispatchedAt: new Date(manifest.dispatchedAt),
        }));
        setEpostManifests(parsedEpostManifests);
      }
    } catch (err) {
      handleError(err);
    }
  }, [sortationId, carrier]);

  const downloadManifest = useCallback(
    async (manifest: ManifestWithAccountDetails | ManifestResponse) => {
      if (
        carrier === ManifestCarrier.DHL &&
        (manifest as ManifestWithAccountDetails).manifestProvider === ManifestProvider.DHL
      ) {
        setManifestsCurrentlyDownloading((prev) => [...prev, (manifest as ManifestWithAccountDetails).id]);
        try {
          const url = await sortationClient.getDhlManifestUrl(
            (manifest as ManifestWithAccountDetails).providerAccountId,
            (manifest as ManifestWithAccountDetails).manifestId
          );
          initiateDownloadFromUrl(
            url,
            `${(manifest as ManifestWithAccountDetails).providerAccountId}_${
              (manifest as ManifestWithAccountDetails).manifestId
            }.pdf`
          );
          addAutoCloseNotification(
            createSuccessNotification(`Manifest ${(manifest as ManifestWithAccountDetails).id} downloaded`),
            TOAST_AUTO_CLOSE
          );
          setManifestsCurrentlyDownloading((prev) =>
            prev.filter((manifestId) => manifestId !== (manifest as ManifestWithAccountDetails).id)
          );
        } catch (err) {
          handleError(err);
        }
      } else if (carrier === ManifestCarrier.RRDONNELLEY) {
        const response: Buffer = await sortationClient.downloadGlobalEManifest((manifest as ManifestResponse).url);
        const blob = new Blob([response], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const filename = `${(manifest as ManifestResponse).gaylordId}_${
          (manifest as ManifestResponse).merchantGuid
        }.pdf`;
        initiateDownloadFromUrl(url, filename);
        addAutoCloseNotification(
          createSuccessNotification(`Manifest ${(manifest as ManifestResponse).gaylordId} downloaded`),
          TOAST_AUTO_CLOSE
        );
      }
    },
    [addAutoCloseNotification, handleError, sortationClient, carrier]
  );

  return {
    manifests,
    epostManifests,
    manifestsCurrentlyDownloading,
    isLoading,
    downloadManifest,
    sortationId,
  };
};
